import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { TabsContent } from '@/components/ui/tabs';
import { Sar } from '@/components/SarIcon';

export default function SalaryTab({ formData, setFormData, formErrors, t, calcTotal, language }) {
  const isAr = language === 'ar';
  return (
    <TabsContent value="salary" className="space-y-3 mt-3 h-[480px] overflow-y-auto pe-1">
      <div className="bg-white rounded-lg border border-[#C4BFF0] p-3 space-y-2">
        <p className="text-xs font-semibold text-[#655dc6]/70 tracking-wide">{t('salary_details')}</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-start block">{t('basic_salary')}</Label>
            <Input type="number" value={formData.basic_salary} onChange={(e) => setFormData(prev => ({ ...prev, basic_salary: e.target.value }))} data-testid="input-basic-salary" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-start block">{t('housing_allowance')}</Label>
            <Input type="number" value={formData.housing_allowance} onChange={(e) => setFormData(prev => ({ ...prev, housing_allowance: e.target.value }))} data-testid="input-housing" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-start block">{t('transport_allowance')}</Label>
            <Input type="number" value={formData.transport_allowance} onChange={(e) => setFormData(prev => ({ ...prev, transport_allowance: e.target.value }))} data-testid="input-transport" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-start block">{t('other_allowances')}</Label>
            <Input type="number" value={formData.other_allowances} onChange={(e) => setFormData(prev => ({ ...prev, other_allowances: e.target.value }))} data-testid="input-other-allowances" />
          </div>
        </div>
        {formData.nationality === 'سعودي' && (
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div className="space-y-1">
              <Label className="text-xs text-start block text-rose-700">
                {isAr ? 'تأمينات اجتماعية (GOSI)' : 'Social Insurance (GOSI)'}
              </Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.gosi_subscription}
                onChange={(e) => setFormData(prev => ({ ...prev, gosi_subscription: e.target.value }))}
                data-testid="input-gosi"
                dir="ltr"
              />
              <p className="text-[10px] text-rose-700/70 leading-tight">
                {isAr
                  ? 'مبلغ مقطوع شهري يُخصم من إجمالي الراتب. يظهر للسعوديين فقط.'
                  : 'Fixed monthly amount deducted from gross. Saudis only.'}
              </p>
            </div>
            <div />
          </div>
        )}
        <Separator />
        <div className="space-y-2">
          <div className="flex justify-between items-center px-2 py-2 bg-[#E8E6F5] rounded-lg">
            <span className="text-sm font-medium text-[#655dc6]">{t('total_salary')}</span>
            <span className="text-lg font-bold text-[#655dc6]" data-testid="total-salary-calc">{calcTotal().toLocaleString()} <Sar /></span>
          </div>
          {formData.nationality === 'سعودي' && (parseFloat(formData.gosi_subscription) || 0) > 0 && (
            <>
              <div className="flex justify-between items-center px-2 py-1.5 text-rose-700">
                <span className="text-xs font-medium">
                  {isAr ? 'خصم: تأمينات اجتماعية (GOSI)' : 'Deduction: GOSI'}
                </span>
                <span className="text-sm font-bold" data-testid="gosi-deduction-calc">
                  − {(parseFloat(formData.gosi_subscription) || 0).toLocaleString()} <Sar />
                </span>
              </div>
              <div className="flex justify-between items-center px-2 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                <span className="text-sm font-semibold text-emerald-800">
                  {isAr ? 'صافي الراتب الشهري' : 'Net Monthly Salary'}
                </span>
                <span className="text-lg font-bold text-emerald-800" data-testid="net-salary-calc">
                  {(calcTotal() - (parseFloat(formData.gosi_subscription) || 0)).toLocaleString()} <Sar />
                </span>
              </div>
            </>
          )}
        </div>
        {formErrors.salary && (
          <p className="text-xs text-red-500 text-start" data-testid="salary-error">{t('salary_required')}</p>
        )}
        <Separator />
        <div className="flex items-center gap-2 pt-1">
          <input
            type="checkbox"
            id="exclude_overtime"
            checked={!!formData.exclude_from_overtime}
            onChange={(e) => setFormData(prev => ({ ...prev, exclude_from_overtime: e.target.checked }))}
            className="h-4 w-4 rounded border-[#C4BFF0] text-[#655dc6] focus:ring-[#655dc6]"
            data-testid="input-exclude-overtime"
          />
          <Label htmlFor="exclude_overtime" className="text-xs text-[#655dc6] cursor-pointer">
            {language === 'ar' ? 'استثناء من احتساب الإضافي' : 'Exclude from Overtime Calculation'}
          </Label>
        </div>
      </div>

      {/* Hint about how leave encashment is computed */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1.5">
        <p className="text-xs font-semibold text-blue-900">
          {isAr ? 'كيف يحسب النظام بدل الإجازة؟' : 'How is Leave Encashment Calculated?'}
        </p>
        {isAr ? (
          <ul className="text-[11px] text-blue-800/90 leading-relaxed space-y-1 ms-3 list-disc">
            <li>
              <strong>راتب واحد ثابت</strong> → حساب تناسبي بسيط على الراتب الحالي.
            </li>
            <li>
              <strong>تغيير راتب أو أكثر</strong> (من تبويب التعديل) → ينتقل النظام تلقائياً إلى FIFO على الرواتب التاريخية.
            </li>
            <li>
              <strong>موظف قديم له رصيد سابق</strong> → أَدخل الفترات يدوياً من تبويب <em>«الرصيد التاريخي»</em> ويتم استهلاك الأقدم أولاً.
            </li>
          </ul>
        ) : (
          <ul className="text-[11px] text-blue-800/90 leading-relaxed space-y-1 ms-3 list-disc">
            <li>
              <strong>Single constant salary</strong> → simple prorated calc on the current rate.
            </li>
            <li>
              <strong>One or more salary changes</strong> (via the edit tab) → auto-switches to FIFO over historical rates.
            </li>
            <li>
              <strong>Legacy hire with prior balance</strong> → enter the period buckets manually from the <em>Legacy Balance</em> tab; oldest is consumed first.
            </li>
          </ul>
        )}
      </div>
    </TabsContent>
  );
}
